/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isfunction from 'lodash.isfunction';
import moment from 'moment';
import {get} from '@ion/core/src/environment/environment';
import {error, info} from '../logger/logger';
import {GenericObject} from '../types/entities';
import {DateType, timeDiff} from '../utils/DateUtil';
import {contains} from '../utils/StringUtil';
import {
  getSisenseOptions,
  getSisenseSession,
  getSisenseToken,
  logout,
  registerToken,
  restoreDashboard,
  switchSisenseRole,
  syncSisenseSession
} from './Reports';
import {createEmbedPath, SisenseOptions, SisenseRole} from './SisenseUtils';

// defines the params definition for the getEmbedURL method
export interface GetEmbedURLParams {
  dashboardId?: string;
  widgetId?: string;
  reportRequestParameters?: string;
  restore?: boolean;
};

// defines the Sisense idle validity in minutes...
export const SISENSE_TOKEN_VALIDITY = 59;

// defines the sisense dashboard ids...
export enum DashboardId {
  InitialDashboard = '____initial_dashboard____',
  DriverScoreV2 = '63ec31ac3551070036638b4d',
  DriverDashboard = '69aaab887a9dfcda33097e02',
  HarshEvents = '6791c74e1e659300334c0a02',
  SafetyBasic = '69df45dd7a9dfcda3309bf14',
  Utilization = '6399023c9ef40d0037448d9d',
  CrashBoxx = '65bb2b71321c430033796939',
  ADMCrashBoxx = '6523c0dd10aae500343ca09b',
  DeviceCommunicationSummary = '64ccb554cc31200033a6489f',
  TireSensor = '64ef1e08b2250d0033de0ac4',
  MonthlyReport = '65167148843d1c0034515605',
  YearlyReport = '65167822843d1c0034515634'
};

// defines the filter type enum...
export enum FilterType {
  DateTime = 'datetime'
};

// defines the Sisense Role state...
export interface SisenseRoleState {
  preState: SisenseRole;
  currentState: SisenseRole;
};

// defines the different type of Sisense embedding methods...
export enum SisenseEmbed {
  IFRAME = 'iframe',
  SDK = 'sdk',
  JS = 'js'
};

// [CORE PACKAGE TODO]: This should be moved to the SisenseUtils.ts Module ...
// internal utility to make some delay...
const makeDelay = (delay = 150) => {
  return new Promise(resolve => {
    const sleep = setTimeout(() => {
      clearTimeout(sleep);
      resolve(true);
    }, delay);
  });
};

/**
 * The Sisense singleton API encapsulates the management of Sisense authentication which includes
 * retrieval, and access of JWT tokens that is basically account specific. When the Client calls the
 * getEmbedURL method, that will automatically handle the session based on it's validity.
 * Also, the JWT will update when a new account has been set and this is achieved whenever
 * we re-set the account we setting null to server options. The API also provides some basic
 * pieces of information such as session and server details.
 *
 * ```typescript
 *
 *  import Sisense from '@ion/core/src/services/Sisense';
 *
 *  // set account id...
 *  Sisense.setAccountId(accontId);
 *
 *  // retrieve sisense session details...
 *  Sisense.isSessionCreated();
 *  Sisense.getServerOptions();
 *
 *  // retrieve Sisense options...
 *  await Sisense.retrieveOptions();
 *
 *  // get the sisense embed url
 *  await Sisense.getEmbedURL({dashboardId, widgetId});
 *
 * ```
 */
class Sisense {
  /**
   * Provides a reference to the Authenticated Users Authentication Token (JWT).
   *
   * This token is retrieved directly from the Sisense Metadata API after all
   * Sisense initializations have been completed, and is primarily used for
   * client-side charting, and / or direct Sisense Metadata API calls.
   */
  private _authToken: string | undefined;

  /**
   * This is a static private variable for storing sisense options
   * and should not be accessed directly.
   */
  private _serverOptions: SisenseOptions;

  /**
   * This is a static private variable for storing current account
   * which is in GGC and should not be accessed directly.
   */
  private _accountId: number;

  /**
   * This is a static private variable for storing sisense
   * user data based on the current account and CTC user
   * account.
   */
  private _settings: GenericObject = {};

  /**
   * This is a static private variable for storing validity of the
   * sisense JWT token and should not be accessed directly.
   */
  private _refreshedTime: DateType;

  /**
   * This is a static private variable for storing sisense session
   * creation detail and should not be accessed directly.
   */
  private _isSessionCreated: boolean;

  /**
   * This is a static private variable for storing sisense option
   * loading status to clients and should not be accessed directly.
   */
  private _isLoading: boolean;

  /**
   * The private variable that used for sisense refresh.
   */
  private _sisenseRefresh: number;

  /**
   * This is a private variable for storing sisense frame.js loaded
   * status to clients and it should not be accessed directly.
   */
  private _preInitialized = false;

  /**
   * This is a private variable for storing sisense sisense.js loaded
   * status to clients and it should not be accessed directly.
   */
  private _postInitialized = false;

  /**
   * This is a private variable for storing sisense frame.js loading
   * status and used inside class.
   */
  private _postInitializationLoading = false;

  /**
   * This is a private variable for storing callback methods into queue While
   * sisense sisense.js loading and it should not be accessed directly.
   */
  private _callbackQueue = [];

  /**
   * This is a private variable for storing Sisense app instance which is
   * required for Sisense JS embedding and it should not be accessed directly.
   */
  private _appInstance = null;

  /**
   * This is a private variable for managing the Sisense user's role state.
   * Type: SisenseRoleState
   */
  private _role: SisenseRoleState = {
    preState     : SisenseRole.Viewer,
    currentState : SisenseRole.Viewer
  };

  /**
   * This is a private variable indicating whether the user is permitted to Sisense.
   */
  private _permitted = true;

  /**
   * This is a private variable holds the callbacks that are needed to be fired when any states updated.
   */
  private _subscribers: (() => void)[] = [];

  /**
   * This is a private variable indicating the error occuring while sisense options retrieved.
   */
  private _sisenseOptionsError = false;

  /**
   * Returns a reference to the Authenticated Users Authentication Token (JWT).
   *
   * This token is retrieved directly from the Sisense Metadata API after all
   * Sisense initializations have been completed, and is primarily used for
   * client-side charting, and / or direct Sisense Metadata API calls.
   */
  public getAuthToken = (): string | undefined => {
    return this._authToken;
  };

  /**
   * The public method allow the client to get the sisense options error
   * has been created based on GGC account.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const sisenseOptionsError = Sisense.getSisenseOptionsError();
   *
   * ```
   */
  getSisenseOptionsError = () => this._sisenseOptionsError;

  /**
   * The public method allow the client to un-subscribe the it when the component mounts. It means
   * this will add the callback instance to the subscribers array.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.subscribe(instance);
   *
   * ```
   */
  subscribe(subscriber: () => void) {
    subscriber && this._subscribers.push(subscriber);
  }

  /**
   * The public method allow the client to un-subscribe the it when the component unmounts. It means
   * this will remove the callback instance from the subscribers array.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.unsubscribe(instance);
   *
   * ```
   */
  unsubscribe(unsubscriber: () => void) {
    this._subscribers = this._subscribers.filter(subscriber => subscriber !== unsubscriber);
  }

  // Private Method to notify subscribed callbacks
  private notifySubscribers() {
    this._subscribers.forEach(subscriber => subscriber());
  }

  /**
   * The public method allow the client to set the account id and clear
   * the existing sisense server options.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.setAccountId(accountId);
   *
   * ```
   */
  setAccountId(accountId: number, refreshSession = false): void {
    this._serverOptions = null;
    this.setRole(SisenseRole.Viewer);
    try {
      this.verifyRole();
    } catch (e) {
      error(e);
    }
    this._accountId = accountId;
    refreshSession && this.getEmbedURL({});
    this.notifySubscribers();
  }

  /**
   * The public method allow the client to know whether the sisense session
   * has been created or not. This method returns the boolean value.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const isSessionCreated = Sisense.isSessionCreated();
   *
   * ```
   */
  isSessionCreated = (): boolean => this._isSessionCreated;

  /**
   * The public method allow the client to get the sisense session details
   * has been created based on GGC account.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const sisenseOptions = Sisense.getServerOptions();
   *
   * ```
   */
  getServerOptions = (): SisenseOptions => this._serverOptions;

  /**
   * The public method allow the client to get the sisense theme id
   * has been created based on GGC account.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const themeId = Sisense.getThemeId();
   *
   * ```
   */
  getThemeId = (): string => this._serverOptions?.themeId;

  /**
   * The public method allow the client to get the sisense user data/settings.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const settings = Sisense.getSettings();
   *
   * ```
   */
  getSettings = (): GenericObject => this._settings;

  /**
   * The public method allow the client to get the sisense API loading status.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const isLoading = Sisense.isLoading();
   *
   * ```
   */
  isLoading = (): boolean => this._isLoading;

  /**
   * The public method allow the client to get the sisense frame.js
   * script loaded status.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const isInitialized = Sisense.isPreInitialized();
   *
   * ```
   */
  isPreInitialized = (): boolean => this._preInitialized;

  /**
   * The public method allow the client to get the sisense sisense.js
   * script loaded status.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const isPostInitialized = Sisense.isPostInitialized();
   *
   * ```
   */
  isPostInitialized = (): boolean => this._postInitialized;

  /**
   * The public method allow the client to get the sisense app
   * instance which is required for SisenseJS embedding type.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const app = Sisense.getAppInstance();
   *
   * ```
   */
  async getAppInstance() {
    if (!this._appInstance) {
      this._appInstance = await this.connectApp();
    }
    return this._appInstance;
  }

  /**
   * Set the permission status.
   *
   * @param permitted - A boolean indicating whether the user is permitted.
   */
  setPermitted(permitted: boolean) {
    this._permitted = permitted;
  };

  /**
   * Check if the user is permitted.
   *
   * @returns True if the user is permitted, false otherwise.
   */
  isPermitted = () => this._permitted;

  /**
   * The public method allow the client to set the sisense settings
   * which is required to set the setting user data at sisense side.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.setSettings({unit_of_measurement: 'US'});
   *
   * ```
   */
  setSettings(settings: GenericObject) {
    this._settings = settings;
  }

  /**
   * The public method allow the client to update the sisense settings
   * which is required to set the setting user data at sisense side. Also,
   * this function will clear the sisense session when the user settings
   * are updated in default.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.updateSettings({unit_of_measurement: 'US'});
   *
   * ```
   */
  updateSettings(settings: GenericObject, refreshSession = false) {
    this._settings = {
      ...this._settings,
      ...settings
    };
    if (refreshSession) {
      this._serverOptions = null;
      this.getEmbedURL({});
    }
    this.notifySubscribers();
  }

  /**
   * This method allows you to set the current Sisense user role.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.setRole(SisenseRole.Designer);
   *
   * ```
   * @param role The new role to assign to the user.
   */
  setRole(role: SisenseRole) {
    this._role = {
      ...this._role,
      currentState : role
    };
  }

  /**
   * This method allows you to get the Sisense user role state.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  Sisense.getRole();
   *
   * ```
   */
  getRole = () => this._role;

  /**
   * The generic private method povides an API for retrieving the sisense options.
   * This method is primarily to be used handles the authentication / authorization
   * error occurring at Meta data server side.
   */
  private async fetchSisenseOptions(retring = false): Promise<any> {
    try {
      this._sisenseOptionsError = false;
      this.notifySubscribers();
      return await getSisenseOptions(this._accountId, this._settings);
    } catch (err) {
      if (!retring && err.errorCode === 403) {
        await registerToken();
        return await this.fetchSisenseOptions(true);
      } else {
        this._sisenseOptionsError = true;
        this.notifySubscribers();
        error(`An Error occurred while retrieving Sisense Options for ${this._accountId}`);
        throw new Error(err);
      }
    }
  };

  /**
   * Provides an API for retrieving the current `processingComplete` sisense options
   * status. This method is primarily to be used for revalidating the `processingComplete`
   * status when it is initially `false` in order to determine if processing has been
   * completed.
   */
  async getReportProcessingStatus(): Promise<boolean> {
    try {
      const {data: {results}} = await this.fetchSisenseOptions();
      const status = results?.processingComplete;
      if (results && this._serverOptions) {
        if (!this._serverOptions.processingComplete && status) {
          if (this.isSessionCreated()) {
            await logout();
          }
          this._serverOptions = results;
          await this.createSisenseSession(true);
          this.notifySubscribers();
        }
      }
      return status;
    } catch (err) {
      error(`An Error occurred while retrieving Sisense Options for ${this._accountId}`);
    }
  };

  /**
   * A private method that creates or refreshes a session with the Sisense server, which is a crucial
   * step for accessing Sisense resources. If `createSession` is set to `true`, it attempts to
   * establish a new session using the provided server options. Upon successful creation or refresh
   * of the session, internal flags and timestamps are updated accordingly.
   *
   * @param createSession Indicates whether to create a new session or not. Default is `true`.
   *                      If `true`, it attempts to create a new session with the provided server options.
   *                      If `false`, it skips the session creation process.
   *
   * @throws An error is thrown if there are issues encountered during the session creation process,
   *         such as network errors or CORS (Cross-Origin Resource Sharing) restrictions.
   *         However, certain types of errors, specifically those related to network issues or CORS policies,
   *         are handled gracefully, considering the session already created.
   *         All other errors are logged for further investigation.
   *
   * @returns A promise that resolves once the session creation or refresh process is completed.
   *          It does not return any value upon successful execution.
   */
  private async createSisenseSession(createSession = true) {
    if (createSession && this._serverOptions) {
      let sessionCreated = false;
      try {
        await getSisenseSession(this._serverOptions);
        this._authToken = await this.retrieveAuthToken();
        await syncSisenseSession(this._authToken as string);
        sessionCreated = true;
      } catch (err) {
        // while creating session, the sisense server may try to redirect,
        // but app will not allow. In this case, we also consider as success case
        // since the session already created...
        if (contains(err.message, 'Network', false) || contains(err.message, 'CORS', false)) {
          sessionCreated = true;
        } else {
          error(err);
        }
      } finally {
        if (sessionCreated) {
          this._isSessionCreated = true;
          this._refreshedTime    = moment.now();
        }
      }
    }
  }

  /**
   * Provides the action to retrieves the sisense options from meta server for getting
   * jwt token and sisense server details to accessing the sisense reports in iframe or
   * accessing sisense APIs
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const sisenseOptions = await Sisense.retrieveOptions();
   *
   * ```
   */
  async retrieveOptions(createSession = true): Promise<SisenseOptions | null> {
    if (this._accountId && this.isPermitted()) {
      this._isLoading = true;
      this.notifySubscribers();
      try {
        if (this.isSessionCreated()) {
          await logout();
        }
        const {data: {results}} = await this.fetchSisenseOptions();
        this._isSessionCreated = false;
        this._refreshedTime    = null;
        this._serverOptions    = results;
      } catch (err) {
        error(err);
        this._serverOptions = this._refreshedTime = null;
      }
      await this.createSisenseSession(createSession);
      this.stopSessionRefresh();
      this.startSessionRefresh(SISENSE_TOKEN_VALIDITY);
      this._isLoading = false;
      this.notifySubscribers();
      return this._serverOptions;
    }
  }

  /**
   * Provides an API for retrieving the Sisense Users Authentication Token (JWT).
   *
   * ```typescript
   *   import Sisense from '@ion/core/src/services/Sisense';
   *   ...
   *
   *   const token = await Sisense.getAuthenticationToken();
   * ```
   *
   * @returns A Promise containing the `authToken` for the authenticated user's
   *          session.
   */
  public async retrieveAuthToken(): Promise<string | undefined> {
    try {
      return await getSisenseToken();
    } catch (err) {
      error(`An Error occurred retrieving the Sisense API Auth Token. ${err.toString()}`);
    }
  }

  /**
   * Provides a consolidation of all Sisense requests required for full authorization.
   *
   * @param   accountId The `accountId` to be used when initializing the user's session.
   * @param   settings  Optional `settings` to be used when initializing the user's session.
   * @param   callback  Optional `callback` function to be executed after the session has
   *                    been initialized. This allows for the method to be executed purely
   *                    async and no blocking.
   * @returns A Promise which resolves to the retrieved Sisense Options, and the
   *          authentication Token when the `includeAuthToken` argument is set to
   *          `true`;
   */
  public async initialize(accountId: number, settings?: GenericObject, callback?: Function): Promise<void> {
    this.setAccountId(accountId);
    try {
      if (settings) {
        await this.updateSettings(settings);
      }
      await registerToken();
      await this.retrieveOptions();
      this.notifySubscribers();
      callback?.();
    } catch (err) {
      error(`An Error occurred during Sisense initialization. ${err.toString()}`);
    }
  }

  /**
   * The public method allow the client to get the sisense
   * embed url based on the dashboard and widget Id. If the method
   * just get the dashboard id, then it returns the dashboard embed url,
   * else it will returns the widget embed url to the client.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const dashboardUrl = await Sisense.getEmbedURL({dashboardId: '61ae95d512b1a20037dbb6bd'});
   *
   *  const widgetUrl = await Sisense.getEmbedURL({
   *    dashboardId: '61ae95d512b1a20037dbb6bd',
   *    widgetId:'431ae35d512b1a20037dbb6bd'
   *  });
   *
   * ```
   */
  async getEmbedURL({
    dashboardId,
    widgetId,
    reportRequestParameters,
    restore = false
  }: GetEmbedURLParams): Promise<string> {
    if (this._accountId && this.isPermitted()) {
      // avoiding mutiple reporting call to get sisense option from different components...
      if (!this.isLoading()) {
        // if the token validity expired or sisense option is null, just
        // update the options from the reporting server...
        const validity = timeDiff(moment.now(), this._refreshedTime, 'm');
        if (!this._serverOptions || validity >= SISENSE_TOKEN_VALIDITY) {
          await this.retrieveOptions();
        }
        await this.verifyRole();
        restore && await restoreDashboard(dashboardId);
        const path = createEmbedPath({
          options          : this._serverOptions,
          isSessionCreated : this._isSessionCreated,
          reportRequestParameters,
          dashboardId,
          widgetId
        });
        this._isSessionCreated = true;
        return path;
      }
      await makeDelay();
      return await this.getEmbedURL({
        dashboardId,
        widgetId,
        reportRequestParameters
      });
    }
  };

  /**
   * This method is used to verify and potentially update the Sisense user's role.
   * It checks if the current role (currentState) is different from the previous
   * role (preState) and, if so, makes an asynchronous call to change the role using
   * the switchSisenseRole function. It handles loading and error states during this process.
   *
   *```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  await Sisense.verifyRole();
   *
   * ```
   */
  async verifyRole() {
    const currentState = this._role.currentState;
    const preState     = this._role.preState;
    if (this.isPermitted() && currentState !== preState) {
      try {
        this._isLoading = true;
        this.notifySubscribers();
        await switchSisenseRole({
          accountId : this._accountId,
          role      : this._role.currentState
        });
        this._role.preState = currentState;
        this._isLoading = false;
        this.notifySubscribers();
      } catch (e) {
        this._isLoading = false;
        this.notifySubscribers();
        throw new Error(e);
      }
    }
  }

  /**
   * Clears the Sisense session and logs the user out. If a Sisense session is created,
   * this method sets the user's role to Viewer, verifies the role change, and initiates
   * the logout process. It's important to note that this method will only execute
   * if a Sisense session exists.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  await Sisense.clearSession();
   *
   * ```
   */
  async clearSession() {
    if (this.isSessionCreated()) {
      try {
        // Set the user's role to Viewer, so they can clear the session.
        // Verify the role change and initiate the logout process.
        this.setRole(SisenseRole.Viewer);
        await Promise.all([
          this.verifyRole(),
          logout()
        ]);
      } catch (err) {
        error(`Clearing sisense session is failed. Reason: ${err.message}`);
      }
    }
  };

  /**
   * The public method allows the client to start the sisense refresh session. If the calle
   * is not passing the interval, the method calculates based on the current JWT validity.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  // starts while component is mounting...
   *  useEffect(() => {
   *    Sisense.startSessionRefresh();
   *  }, []);
   *
   * ```
   */
  startSessionRefresh(interval?: number) {
    if (!this._sisenseRefresh) {
      const validity = timeDiff(moment.now(), this._refreshedTime, 'm');
      const refresh  = interval ?? SISENSE_TOKEN_VALIDITY - validity;
      this._sisenseRefresh = setTimeout(() => {
        this.retrieveOptions();
      }, refresh * 60 * 1000) as unknown as number;
    }
  }

  /**
   * The public method allows the client to stop the sisense refresh session, if
   * auto referesh is in the progress.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  // stops while component is unmounting...
   *  useEffect(() => {
   *    return () => {
   *      Sisense.startSessionRefresh();
   *    }
   *  }, []);
   *
   * ```
   */
  stopSessionRefresh() {
    if (this._sisenseRefresh) {
      clearTimeout(this._sisenseRefresh);
      this._sisenseRefresh = null;
    }
  }

  /**
   * The public method allows the client to load the sisense frame.js at run time
   * with different url based on the environment. This is required for the
   * embedding sisense dashboard/widget via Sisense SDK.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const App = () => {
   *    useEffect(() => {
   *      Sisense.injectScript();
   *    }, []);
   *
   *    return (<Component />);
   *  }
   *
   * ```
   */
  injectFrameScript(window = globalThis) {
    const injector = window.__initializeSisenseJS;
    if (isfunction(injector)) {
      try {
        injector(`${get('sisenseBaseURL')}/js/frame.js`);
        this._preInitialized = true;
        info('Sisense frame script injected successfully');
      } catch (err) {
        error('Sisense frame script injection failed: ' + err);
      }
    } else {
      error('The sisense script vendor is unavailable');
    }
  }

  /**
   * The public method allows the client to load the sisense.js at run time
   * with different url based on the environment. This is required for the
   * embedding sisense dashboard/widget via SisenseJS.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const App = () => {
   *    useEffect(() => {
   *      Sisense.injectSisenseScript();
   *    }, []);
   *
   *    return (<Component />);
   *  }
   *
   * ```
   */
  injectSisenseScript(window = globalThis, callback?: Function) {
    const injector = window.__initializeSisenseJS;
    // once sisense.js script loaded or being load, we must not allow other
    // callee to inject again which may affect the performance of the app.
    if (!(this._postInitializationLoading || this._postInitialized)) {
      if (isfunction(injector)) {
        this._postInitializationLoading = true;
        callback && this._callbackQueue.push(callback);
        try {
          // this should be loaded post sisense authentication...
          injector(`${get('sisenseBaseURL')}/js/sisense.js`, async() => {
            this._postInitialized = true;
            info('Sisense script injected successfully');
            await this.connectApp();
            // make call all the callback functions which is in queue...
            this._callbackQueue.forEach(cb => cb());
            // make empty the queue once all callbacks has been called...
            this._callbackQueue.splice(0, this._callbackQueue.length);
            this._postInitializationLoading = false;
          });
        } catch (err) {
          error('Sisense script injection failed: ' + err);
        }
      } else {
        error('The sisense script vendor is unavailable');
      }
    } else {
      callback && this._callbackQueue.push(callback);
    }
  }

  /**
   * The method allows us to connect to the Sisense(Sisense JS embed type). After included
   * the Sisense.js library in our site, we must connect to Sisense. The method returns
   * a promise that resolves to app object. The app objects contains your dashboards and
   * defines how your widgets are embedded.
   *
   * ```typescript
   *
   *  import Sisense from '@ion/core/src/services/Sisense';
   *
   *  const app = await connectApp(); // sisense app instance
   *
   * ```
   */
  async connectApp(window = globalThis) {
    // Sisense container elements and other DOM here
    let appEle = document.getElementById('sisenseApp');
    appEle?.remove();
    appEle = document.createElement('div');
    appEle.style.display = 'none';
    appEle.id = 'sisenseApp';
    document.body.appendChild(appEle);
    // make sure the sisenseApp element and other container elements
    // are loaded before using the Sisense.connect function, by explicitly
    // creating it as an empty container within page.
    try {
      const Sisense = window.Sisense;
      if (Sisense) {
        this._appInstance = await Sisense.connect(get('sisenseBaseURL'), false);
      } else {
        error('Sisense script is not injected');
      }
    } catch (err) {
      error('Sisense App is not connected, Reason: ', err);
    }
    return this._appInstance;
  }
}

export default new Sisense();
